/**
 * Calculadora de precio de venta (Desarrollo de Producto). Costea TODO lo que compone el precio de un
 * producto en Mercado Libre Chile y despeja el precio de venta necesario para un margen objetivo, o —
 * al revés — el margen que deja un precio dado. Toda la matemática vive aquí (pura y testeable); el
 * panel de React solo la usa.
 *
 * Valores reales de referencia de MELI Chile (2025-2026), EDITABLES por el usuario porque cambian y ML
 * pide confirmarlos en su simulador oficial:
 *  - Comisión por venta: 13%–20% según categoría y tipo (clásica/premium).
 *  - Cargo fijo por unidad (~$600 CLP) en productos bajo cierto umbral (~$19.990 CLP).
 *  - IVA 19% aplica sobre las tarifas de ML (comisión, publicidad); es crédito fiscal si declaras IVA.
 * Fuentes: developers/ayuda de Mercado Libre + guías de costos MELI Chile 2025-2026.
 */

/** IVA Chile. */
export const IVA_CL = 0.19;

/** Cómo se cobra la publicidad (Product Ads): como % del precio o como monto fijo por unidad. */
export type AdsMode = "pct" | "amount";

export interface PriceCalcInput {
  /** Costo del proveedor por unidad (CLP), tal como lo pagas. */
  readonly providerCost: number;
  /** Comisión de venta de la categoría de ML (%). EDITABLE. */
  readonly commissionPct: number;
  /** Cargo fijo por unidad de ML (CLP) para productos de bajo precio. */
  readonly fixedFee: number;
  /** Umbral de precio (CLP) bajo el cual aplica el cargo fijo. Por encima, no se cobra. */
  readonly fixedFeeThreshold: number;
  /** Publicidad: modo y valor (% del precio, o monto fijo en CLP). */
  readonly adsMode: AdsMode;
  readonly ads: number;
  /** Embalaje por unidad (CLP). */
  readonly packaging: number;
  /** Envío que ASUMES tú por unidad (CLP). 0 si el comprador lo paga. */
  readonly shipping: number;
  /** Otros costos por unidad (CLP): etiquetas, mano de obra, etc. */
  readonly otherCost: number;
  /** Si el IVA 19% se suma sobre las tarifas de ML (comisión, cargo fijo, ads). */
  readonly ivaOnFees: boolean;
  /** Margen objetivo como % del precio de venta. */
  readonly targetMarginPct: number;
}

export interface CostLine { readonly label: string; readonly amount: number; readonly kind: "cost" | "profit" }

export interface PriceCalcResult {
  /** Precio de venta sugerido (CLP, redondeado a peso). null si es imposible (fees+margen ≥ 100%). */
  readonly suggestedPrice: number | null;
  /** Desglose línea por línea al precio sugerido. */
  readonly lines: readonly CostLine[];
  /** Utilidad por unidad (CLP) al precio sugerido. */
  readonly profit: number;
  /** Margen real resultante (% sobre precio), tras redondeos. */
  readonly marginPct: number;
  /** Markup sobre el costo total (%). */
  readonly markupPct: number;
  /** Si el cargo fijo de ML se aplicó (precio bajo el umbral). */
  readonly fixedFeeApplied: boolean;
  /** Mensaje de imposibilidad, si suggestedPrice es null. */
  readonly warning?: string;
}

const ivaFactor = (on: boolean): number => (on ? 1 + IVA_CL : 1);
const clamp0 = (n: number): number => (Number.isFinite(n) && n > 0 ? n : 0);

/** Suma de costos FIJOS por unidad (no dependen del precio), dado si aplica el cargo fijo de ML. */
function fixedCosts(i: PriceCalcInput, applyFee: boolean): number {
  const f = ivaFactor(i.ivaOnFees);
  return (
    clamp0(i.providerCost) +
    clamp0(i.packaging) +
    clamp0(i.shipping) +
    clamp0(i.otherCost) +
    (i.adsMode === "amount" ? clamp0(i.ads) * f : 0) +
    (applyFee ? clamp0(i.fixedFee) * f : 0)
  );
}

/** Fracción de costos que dependen del PRECIO (comisión + ads%), ya con IVA si corresponde. */
function percentFees(i: PriceCalcInput): number {
  const f = ivaFactor(i.ivaOnFees);
  const commission = clamp0(i.commissionPct) / 100 * f;
  const adsPct = i.adsMode === "pct" ? clamp0(i.ads) / 100 * f : 0;
  return commission + adsPct;
}

/** Despeja el precio para un margen objetivo: P = costosFijos / (1 − fees% − margen%). */
function solvePrice(i: PriceCalcInput, applyFee: boolean): number | null {
  const denom = 1 - percentFees(i) - clamp0(i.targetMarginPct) / 100;
  if (denom <= 0) return null;
  return fixedCosts(i, applyFee) / denom;
}

/** Desglosa costos y utilidad a un precio dado. */
export function breakdownAtPrice(i: PriceCalcInput, price: number): { lines: CostLine[]; profit: number; marginPct: number; markupPct: number; fixedFeeApplied: boolean } {
  const f = ivaFactor(i.ivaOnFees);
  const applyFee = price < clamp0(i.fixedFeeThreshold);
  const commission = price * (clamp0(i.commissionPct) / 100) * f;
  const adsCost = i.adsMode === "pct" ? price * (clamp0(i.ads) / 100) * f : clamp0(i.ads) * f;
  const fee = applyFee ? clamp0(i.fixedFee) * f : 0;
  const lines: CostLine[] = [
    { label: "Costo proveedor", amount: clamp0(i.providerCost), kind: "cost" },
    { label: `Comisión ML (${clamp0(i.commissionPct)}%${i.ivaOnFees ? " +IVA" : ""})`, amount: commission, kind: "cost" },
    ...(fee > 0 ? [{ label: `Cargo fijo ML${i.ivaOnFees ? " +IVA" : ""}`, amount: fee, kind: "cost" as const }] : []),
    { label: `Publicidad / Ads${i.adsMode === "pct" ? ` (${clamp0(i.ads)}%)` : ""}`, amount: adsCost, kind: "cost" },
    { label: "Embalaje", amount: clamp0(i.packaging), kind: "cost" },
    ...(clamp0(i.shipping) > 0 ? [{ label: "Envío asumido", amount: clamp0(i.shipping), kind: "cost" as const }] : []),
    ...(clamp0(i.otherCost) > 0 ? [{ label: "Otros costos", amount: clamp0(i.otherCost), kind: "cost" as const }] : []),
  ];
  const totalCost = lines.reduce((s, l) => s + l.amount, 0);
  const profit = price - totalCost;
  const marginPct = price > 0 ? (profit / price) * 100 : 0;
  const markupPct = totalCost > 0 ? (profit / totalCost) * 100 : 0;
  return { lines, profit, marginPct, markupPct, fixedFeeApplied: applyFee };
}

/**
 * Calcula el precio de venta sugerido para el margen objetivo. Resuelve la circularidad del cargo fijo
 * (depende del precio, que depende del cargo fijo) probando ambos escenarios y quedándose con el que es
 * consistente con su propio umbral.
 */
export function calculatePrice(i: PriceCalcInput): PriceCalcResult {
  const withFee = solvePrice(i, true);
  const withoutFee = solvePrice(i, false);
  if (withFee === null || withoutFee === null) {
    return { suggestedPrice: null, lines: [], profit: 0, marginPct: 0, markupPct: 0, fixedFeeApplied: false,
      warning: "La comisión + publicidad + margen objetivo suman 100% o más del precio. Baja el margen o revisa los porcentajes." };
  }
  const threshold = clamp0(i.fixedFeeThreshold);
  // Elige el escenario consistente: si al aplicar el cargo el precio queda BAJO el umbral, es válido.
  let price: number;
  let applied: boolean;
  if (withFee < threshold) { price = withFee; applied = true; }          // con cargo, queda bajo umbral → coherente
  else if (withoutFee >= threshold) { price = withoutFee; applied = false; } // sin cargo, queda sobre umbral → coherente
  else { price = withFee; applied = true; }                               // zona límite: usa el escenario con cargo (conservador)

  const rounded = Math.round(price);
  const b = breakdownAtPrice(i, rounded);
  const lines: CostLine[] = [...b.lines, { label: "Utilidad", amount: b.profit, kind: "profit" }];
  return {
    suggestedPrice: rounded, lines, profit: Math.round(b.profit),
    marginPct: Math.round(b.marginPct * 10) / 10, markupPct: Math.round(b.markupPct * 10) / 10,
    fixedFeeApplied: applied,
  };
}

/** Valores por defecto reales de referencia para MELI Chile (todos editables por el usuario). */
export const DEFAULT_CALC_INPUT: PriceCalcInput = {
  providerCost: 0,
  commissionPct: 14,        // dentro del rango 13%–20% de MELI Chile
  fixedFee: 600,            // ~$600 CLP por unidad bajo el umbral
  fixedFeeThreshold: 19990, // aplica bajo ~$19.990 CLP
  adsMode: "pct",
  ads: 0,
  packaging: 0,
  shipping: 0,
  otherCost: 0,
  ivaOnFees: true,          // las tarifas de ML llevan IVA
  targetMarginPct: 30,
};
